/* eslint-disable no-underscore-dangle */

import { types, flow, getRoot, getSnapshot } from "mobx-state-tree"
import { reaction } from "mobx"
import { parsePhoneNumber } from "libphonenumber-js"

import TRACKING from "~/services/tracking"
import { getMessageId } from "~/services/tracking/posthog"
import { createPaymentFlowActions, showCkoFlowDropin } from "./paymentFlowActions"
import {
  REQUEST_STATUSES,
  PAYMENT_PROVIDERS,
  CITY_PAY_CARD_DECLINED,
} from "~/utilities/constants"
import { resolveChannel } from "~/utilities/adAttribution"
import objectIsEmpty from "~/utilities/objectIsEmpty"

import BraintreeStore from "./models/BraintreeStore"
import StripeStore from "./models/StripeStore"
import CKOStore from "./models/CKOStore"
import AddressModel from "./models/AddressModel"
import threeDSecure from "./models/threeDSecureStore"
import * as PaymentService from "~/services/payments"
import * as UserService from "~/services/user"
import {
  parkingCheckoutAcceptances,
  trafficCheckoutAcceptances,
} from "~/utilities/legalDocuments"
import * as StringTools from "~/utilities/string"
import socketCityPayBot from "~/services/paybot/socketCityPayBot"
import {
  loadCheckoutForm,
  saveCheckoutFormToStorage
} from "~/utilities/checkoutPersistence"
import { trackMetaBrowserAddPaymentInfo } from "~/utilities/metaCapiBrowserEvents"

// Disposer for the checkout-form autosave reaction (one per store instance).
let checkoutFormAutosaveDisposer = null

const PERSISTED_BILLING_FIELDS = [
  "billingAddressLine1",
  "billingAddressLine2",
  "billingAddressCity",
  "billingAddressState",
  "billingAddressZip",
  "billingAddressCountry",
]

const PERSISTED_USER_FIELDS = ["name", "email", "phoneNumber"]

// Safely reach the auth user (always present in practice, but guarded so a
// missing store can never crash persistence).
const getAuthUser = (store) => {
  const auth = getRoot(store) && getRoot(store).auth
  return auth && auth.user ? auth.user : null
}

function getCkoFlowDropinContext(rootStore) {
  const { parkingFlow, ui } = rootStore
  return {
    isFightFlow: parkingFlow?.flow?.isFightFlow,
    isTrafficFlow: ui.isTrafficFlow,
    isPayFlow: parkingFlow?.flow?.isPayFlow,
    isRobotPayFlow: parkingFlow?.flow?.isRobotPayFlow,
    isPayFlowFromMobile: parkingFlow?.flow?.isPayParkingFromMobile,
  }
}

function shouldSkipDefaultPaymentMethodFetch(paymentProcessor, rootStore) {
  if (paymentProcessor === PAYMENT_PROVIDERS.cko) return true
  return showCkoFlowDropin(paymentProcessor, getCkoFlowDropinContext(rootStore))
}

const NotPaidTicket = types.model({
  ticketID: types.string,
  error: types.string,
})
export const PaymentStore = types
  .model("PaymentStore", {
    threeDSecure: types.optional(threeDSecure, {}),
    // Is the payment store initialized (called on component mount)
    initialized: types.optional(types.boolean, false),

    // Stores for payment providers (with single interface to interact with different providers)
    [PAYMENT_PROVIDERS.braintree]: types.optional(BraintreeStore, {}),
    [PAYMENT_PROVIDERS.stripe]: types.optional(StripeStore, {}),
    [PAYMENT_PROVIDERS.cko]: types.optional(CKOStore, {}),
    [PAYMENT_PROVIDERS.evervault]: types.optional(CKOStore, {}),
    // User Info
    addressChange: types.optional(AddressModel, {}),
    // User Default Payment
    defaultPaymentMethod: types.maybeNull(types.frozen({})),
    defaultPaymentMethodStatus: types.optional(
      types.string,
      REQUEST_STATUSES.init
    ),
    // Booleans
    _isLoading: types.optional(types.boolean, false), // for internal use, see getter below
    isAddingNewPaymentMethod: types.optional(types.boolean, false),
    isPaymentMethodJustAdded: types.optional(types.boolean, false),
    isChangingAddress: types.optional(types.boolean, false),
    requireAddress: types.optional(types.boolean, true),
    agreedToTermsOfService: types.optional(types.boolean, false),
    // Form inputs
    cardholderName: types.optional(types.string, ""),
    cardholderFirstName: types.optional(types.string, ""),
    cardholderLastName: types.optional(types.string, ""),
    // is tos required
    isTosRequired: types.optional(types.boolean, true),
    // tos error message
    tosError: types.maybeNull(types.string),
    cityPayModalContent: types.optional(types.string, ""),
    cityPayConfirmationID: types.optional(types.string, ""),
    cityPayError: types.optional(types.frozen({}), ""),
    cityPayNotPaidList: types.optional(types.array(NotPaidTicket), []),
    _isCityPayBotRunning: types.optional(types.boolean, false), // for internal use, see getter below
    // Stripe ACH
    isACH: types.optional(types.boolean, false),
    bankAccountLinked: types.optional(types.boolean, false),
    bankAccountPaymentMethod: types.optional(types.string, ""),
    _flowSessionInitialized: types.optional(types.boolean, false),
    _isFlowInitializing: types.optional(types.boolean, false),
    _flowPaymentSessionToken: types.optional(types.string, ""),
    _dashboardSaveComplete: types.optional(types.boolean, false),
  })
  .volatile(() => ({
    checkoutFunction: async () => {}, // async function to call to checkout
    reaction: () => {},
    ref3DSMobileRef: null, // where 3DS iFrame will be appended or shown.
    checkoutInstance: null,
    flowComponent: null,
    _flowPaymentSessionData: null,
    _flowTicketIds: [],
    _flowType: "pay",
    _flowContainerId: "flow-container",
  }))
  .views((self) => ({
    get isLoggedIn() {
      const rootStore = getRoot(self)
      return rootStore.auth.isLoggedIn
    },
    get user() {
      const rootStore = getRoot(self)
      return rootStore.auth.user
    },
    // Mirrors the copy shown next to the TOS checkbox in each checkout skin.
    get tosAcceptances() {
      if (!self.isTosRequired || !self.agreedToTermsOfService) return []
      const { ui, parkingFlow, trafficLookup, trafficSubmission } = getRoot(self)
      try {
        if (ui?.isTrafficFlow) {
          return trafficCheckoutAcceptances({
            isWinitLaw:
              trafficLookup?.isStateAffiliatedWithWinitLaw ||
              trafficSubmission?.isStateAffiliatedWithWinitLaw,
            isPaymentPlan: trafficSubmission?.isOneStepPaymentPlanCheckout,
          })
        }
        return parkingCheckoutAcceptances(parkingFlow?.flow?.isPayFlow)
      } catch (error) {
        return []
      }
    },
    get allowSubmit() {
      const allowSubmit = !!(
        (self.isTosRequired ? self.agreedToTermsOfService : true) &&
        self.initialized &&
        !self._isLoading &&
        self.isReady &&
        (self.isAddingNewPaymentMethod ? self.isValid : true) &&
        self.user &&
        (!self.isLoggedIn ? !self.user.registrationFieldsHaveError : true) &&
        (self.isACH ? self.bankAccountLinked : true) &&
        (self.requireAddress
          ? self.isChangingAddress
            ? self.addressChange.isAddressValid
            : self.user.hasFullAddress
          : true)
      )
      return allowSubmit
    },
    // Dev-only: mirrors the allowSubmit gates and returns the human-readable
    // reasons the pay button is currently disabled. Empty array when submittable.
    get submitDisabledReasons() {
      const { auth } = getRoot(self)
      const reasons = []
      if (self.isTosRequired && !self.agreedToTermsOfService) {
        reasons.push("Terms of Service not agreed")
      }
      if (!self.initialized) reasons.push("payment store not initialized")
      if (self._isLoading) reasons.push("request loading in progress")
      if (!self.isReady) {
        reasons.push(`payment not ready (isReady=false, processor=${self.paymentProcessor})`)
      }
      if (
        self.paymentProcessor !== PAYMENT_PROVIDERS.braintree &&
        self.isAddingNewPaymentMethod &&
        !self.isValid
      ) {
        reasons.push("card not valid/complete (evervault isValid=false)")
      }
      if (!self.user) reasons.push("no user")
      if (!self.isLoggedIn) {
        const fields = ["name", "username", "phoneNumber", "email"].concat(
          auth && auth.canSignUpWithoutPassword ? [] : ["password"]
        )
        const errored = self.user
          ? fields.filter((key) => self.user.validateKey(key))
          : fields
        if (errored.length) {
          reasons.push(`registration field errors: ${errored.join(", ")}`)
        }
      }
      if (self.isACH && !self.bankAccountLinked) {
        reasons.push("ACH bank account not linked")
      }
      if (self.requireAddress) {
        if (self.isChangingAddress) {
          if (!self.addressChange.isAddressValid) reasons.push("address change invalid")
        } else if (!self.user.hasFullAddress) {
          reasons.push("user address incomplete")
        }
      }
      return reasons
    },
    get isValid() {
      const { parkingFlow, ui } = getRoot(self)
      if (!self.paymentProcessor) return false
      if (self.isACH) return true
      if (
        self.paymentProcessor === PAYMENT_PROVIDERS.evervault &&
        (parkingFlow.flow.isFightFlow || ui.isTrafficFlow)
      ) {
        if (process.env.ENABLE_CKO_PAYMENTS === "true")
          return self[PAYMENT_PROVIDERS.cko].isValid
        if (process.env.ENABLE_STRIPE_PAYMENTS === "true")
          return self[PAYMENT_PROVIDERS.stripe].isValid
        return self[PAYMENT_PROVIDERS.braintree].isValid
      }
      return self[self.paymentProcessor].isValid
    },
    get isReady() {
      const { parkingFlow, ui } = getRoot(self)
      if (self.isACH) return true
      if (!self.paymentProcessor) return false
      const processor = self.paymentProcessor
      if (
        processor === PAYMENT_PROVIDERS.cko ||
        (processor === PAYMENT_PROVIDERS.evervault &&
          process.env.ENABLE_CKO_PAYMENTS === "true" &&
          (parkingFlow.flow.isFightFlow || ui.isTrafficFlow))
      ) {
        // CKO Flow tokenizes through the mounted flowComponent (submitPayment
        // throws if it is null). When the component isn't mounted yet — initial
        // mount window, teardown, or a re-init — we are NOT ready. Returning
        // false here keeps the pay button disabled instead of falling through
        // to a stale `cko.isReady`, which would enable the button and then
        // fail at click time with "Payment form is not ready yet".
        if (!self.flowComponent) {
          return false
        }
        return self._flowSessionInitialized && self.cko.isReady
      }
      if (
        processor === PAYMENT_PROVIDERS.evervault &&
        (parkingFlow.flow.isFightFlow || ui.isTrafficFlow)
      ) {
        if (process.env.ENABLE_CKO_PAYMENTS === "true")
          return self[PAYMENT_PROVIDERS.cko].isReady
        if (process.env.ENABLE_STRIPE_PAYMENTS === "true")
          return self[PAYMENT_PROVIDERS.stripe].isReady
        return self[PAYMENT_PROVIDERS.braintree].isReady
      }
      return self[self.paymentProcessor].isReady
    },
    get isLoading() {
      // Checks if the payment store is currently loading, and if we can allow
      // the user to click a "checkout" button / continue to payment / update payment method.

      let isLoading = self._isLoading
      if (self.paymentProcessor === PAYMENT_PROVIDERS.braintree) {
        isLoading = isLoading || self.braintree.isLoadingForm
      }
      return isLoading
    },
    get collapsePaymentFormWhileLoading() {
      const rootStore = getRoot(self)
      const ckoDropinActive =
        showCkoFlowDropin(self.paymentProcessor, getCkoFlowDropinContext(rootStore)) &&
        (self._flowSessionInitialized || self.flowComponent)
      if (ckoDropinActive) {
        return false
      }
      return self.isLoading
    },
    get cityPayBotError() {
      return self.cityPayError
    },
    get isCityPayBotRunning() {
      const isCityPayBotRunning = self._isCityPayBotRunning
      return isCityPayBotRunning
    },
    get hasDefaultPaymentMethod() {
      return self.defaultPaymentMethod && self.defaultPaymentMethod.paymentType
    },
    get paymentProcessor() {
      if (!self.user) return undefined
      if (!self.user.paymentProcessor) return undefined
      if (!self.user.paymentProcessor.type) return undefined
      return self.user.paymentProcessor.type
    },
    get customerId() {
      if (!self.user) return undefined
      if (!self.user.paymentProcessor) return undefined
      if (!self.user.paymentProcessor.id) return undefined
      return self.user.paymentProcessor.id
    },
    get firstNameOnCard() {
      return self.cardholderFirstName
    },
    get lastNameOnCard() {
      return self.cardholderLastName
    },
  }))
  .actions((self) => ({
    initialize: flow(function* initGen({
      checkoutFunction,
      use3DS,
      get3DSVerifyAmount,
      isTosRequired,
    }) {
      if (!checkoutFunction) return false
      self._isLoading = true
      self.isChangingAddress = !self.user.hasFullAddress
      self.checkoutFunction = checkoutFunction
      self.threeDSecure.setIsRequired(!!use3DS)
      self.setIsTosRequired(!!isTosRequired)
      self.setRequireAddress(false)
      self.setCardholderFirstName(self.user.firstName)
      self.setCardholderLastName(self.user.lastName)
      // Prefill any empty checkout fields from a previous (abandoned) flow.
      self.restoreCheckoutForm()
      if (get3DSVerifyAmount) {
        self.threeDSecure.setGet3DSVerifyAmount(get3DSVerifyAmount)
      }
      if (!self.initialized) {
        // todo: See if we can move this call to setInitialized() beneath the error handler.
        self.setInitialized(true)
        try {
          if (!self.paymentProcessor || !self.customerId) {
            yield self.user.fetchCurrentUserData()
          }
          if (self[self.paymentProcessor].initialize) {
            yield self[self.paymentProcessor].initialize()
          }

          const { ui, parkingFlow } = getRoot(self)
          if (
            (ui.isPayNYCFlow &&
              process.env.ENABLE_CKO_PAYMENTS === "true" &&
              !self.isLoggedIn) ||
            (["test", "development"].includes(process.env.NODE_ENV) &&
              process.env.ENABLE_CKO_PAYMENTS === "true" &&
              !self.isLoggedIn) ||
            (ui.currentHost.includes("staging") &&
              process.env.ENABLE_CKO_PAYMENTS === "true" &&
              !self.isLoggedIn)
          ) {
            // Required to do that bc staging is overriden in NODE_ENV for staging
            self.user.paymentProcessor.setType(PAYMENT_PROVIDERS.cko)
          }
          const isWebPayFlow =
            parkingFlow.flow.isPayFlow || parkingFlow.flow.isRobotPayFlow
          const isMobilePayFlow = parkingFlow.flow.isPayParkingFromMobile

          if (isWebPayFlow || isMobilePayFlow) {
            const shouldUserBeEvervaulted = yield self.shouldUserBeEvervaulted()
            if (shouldUserBeEvervaulted) {
              self.user.paymentProcessor.setType(PAYMENT_PROVIDERS.evervault)
            }
          }
          yield self.fetchDefaultMethod()
        } catch (err) {
          self.setInitialized(false)
          if (process.env.WINIT_ENV !== "test") {
            console.error("error initializing payment store", err)
          }
        }
        self.startCheckoutFormAutosave()
        self.callReaction()
        self._isLoading = false
        return true
      }
      self._isLoading = false
      return false
    }),
    setCardholderFirstName(value) {
      self.cardholderFirstName = value
    },
    setCardholderLastName(value) {
      self.cardholderLastName = value
    },
    // Prefill empty checkout fields from localStorage so a user who abandons
    // and restarts the flow does not retype their cardholder/billing info.
    // Only fills fields that are still empty (never overwrites values already
    // set, e.g. a logged-in user's account name).
    restoreCheckoutForm() {
      try {
        const saved = loadCheckoutForm()
        if (!saved) return
        if (!self.cardholderFirstName && saved.cardholderFirstName) {
          self.setCardholderFirstName(saved.cardholderFirstName)
        }
        if (!self.cardholderLastName && saved.cardholderLastName) {
          self.setCardholderLastName(saved.cardholderLastName)
        }
        PERSISTED_BILLING_FIELDS.forEach((field) => {
          if (saved[field] && !self.cko[field]) {
            self.cko.setField(field, saved[field])
          }
        })
        const user = getAuthUser(self)
        if (user) {
          PERSISTED_USER_FIELDS.forEach((field) => {
            if (saved[field] && !user[field]) {
              // Restore email via setEmail so it mirrors into `username`
              // (same as live typing in Email.jsx). changeKey only sets the
              // single key, which leaves username empty and fails the
              // registrationFieldsHaveError gate (pay button stays disabled).
              if (field === "email") {
                user.setEmail(saved[field])
              } else {
                user.changeKey(field, saved[field])
              }
            }
          })
        }
      } catch (e) {
        // Restore is best-effort; never let it break checkout.
      }
    },
    saveCheckoutForm() {
      const user = getAuthUser(self)
      saveCheckoutFormToStorage({
        cardholderFirstName: self.cardholderFirstName,
        cardholderLastName: self.cardholderLastName,
        billingAddressLine1: self.cko.billingAddressLine1,
        billingAddressLine2: self.cko.billingAddressLine2,
        billingAddressCity: self.cko.billingAddressCity,
        billingAddressState: self.cko.billingAddressState,
        billingAddressZip: self.cko.billingAddressZip,
        billingAddressCountry: self.cko.billingAddressCountry,
        name: user ? user.name : "",
        email: user ? user.email : "",
        phoneNumber: user ? user.phoneNumber : "",
      })
    },
    startCheckoutFormAutosave() {
      if (checkoutFormAutosaveDisposer) checkoutFormAutosaveDisposer()
      let autosaveTimer
      checkoutFormAutosaveDisposer = reaction(
        () => {
          const user = getAuthUser(self)
          return [
            self.cardholderFirstName,
            self.cardholderLastName,
            ...PERSISTED_BILLING_FIELDS.map((field) => self.cko[field]),
            ...(user
              ? PERSISTED_USER_FIELDS.map((field) => user[field])
              : PERSISTED_USER_FIELDS.map(() => "")),
          ]
        },
        () => {
          clearTimeout(autosaveTimer)
          autosaveTimer = setTimeout(() => self.saveCheckoutForm(), 500)
        },
        { fireImmediately: false }
      )
    },
    setIsCityPayBotRunning(value) {
      self._isCityPayBotRunning = value
    },
    setCityPayConfirmationID(value) {
      self.cityPayConfirmationID = value
    },
    callReaction() {
      self.reaction = reaction(
        () => self.user.objectId,
        () => {
          self.teardown()
          self.initialize({
            checkoutFunction: self.checkoutFunction,
            use3DS: self.threeDSecure.isRequired,
            get3DSVerifyAmount: self.threeDSecure.get3DSVerifyAmount,
          })
        }
      )
    },
    shouldUserBeEvervaulted: flow(function* shouldUserBeEvervaultedGen() {
      try {
        const rootStore = getRoot(self)
        return yield PaymentService.shouldUserBeEvervaulted(
          rootStore.auth.user.sessionToken,
          rootStore.auth.user.email
        )
      } catch (error) {
        console.error(
          "error when trying to determine if user can be evervaulted",
          error
        )
      }
    }),
    preAuthorizeChargeWithEvByCardValues: flow(function*(
      cardholderFirstName,
      cardholderLastName,
      cardNumberAlias,
      cardExpDate,
      cardCvcAlias,
      cardBrand
    ) {
      if (self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault) {
        const rootStore = getRoot(self)
        const { isPayNYCFlow } = rootStore.ui
        const source = isPayNYCFlow ? "payNYC" : "webapp"
        // Normalized acquisition channel ('ads'|'email'|'sms'|'organic') resolved from
        // the click id / utm captured at the ad click, forwarded alongside gclid.
        // auth.utmParameters is a frozen {} default, always truthy — `||` would never
        // fall through to the User's utm, which is where handleUtmParameters actually
        // writes fresh utm for logged-in users (see pages/_app.jsx). Check emptiness
        // instead so a logged-in user's real utm_medium is read.
        const utm = !objectIsEmpty(rootStore.auth.utmParameters)
          ? rootStore.auth.utmParameters
          : rootStore.auth.user && rootStore.auth.user.utm
        const channel = resolveChannel({
          gclid: rootStore.auth.gclid || (rootStore.auth.user && rootStore.auth.user.gclid),
          msclkid: rootStore.auth.msclkid || (rootStore.auth.user && rootStore.auth.user.msclkid),
          utm,
          gclidCapturedAt: rootStore.auth.gclidCapturedAt,
        })
        const utmCampaign = (utm && utm.utm_campaign) || undefined
        const {
          billingAddressLine1,
          billingAddressLine2,
          billingAddressCity,
          billingAddressState,
          billingAddressZip,
          billingAddressCountry,
        } = rootStore.payment.cko
        const firstPlateNumber = rootStore.parkingFlow.flow?.plate?.plateNumber
        const modifiedBillingZip = billingAddressZip.trim()
        // Both the Evervault preAuthorize and CityPay backends take a single
        // address line, so fold the optional Apt/Unit/Suite into it. Card AVS
        // only checks the street number + ZIP, so the unit does not affect the
        // AVS result and is preserved here for the billing record.
        const fullBillingAddress = [billingAddressLine1, billingAddressLine2]
          .filter(Boolean)
          .join(" ")
          .trim()
        try {
          return yield PaymentService.preAuthorizeChargeEvByCardValues(
            rootStore.auth.user.sessionToken,
            rootStore.auth.user.email, // For payNYCFlow when users does not sign in
            rootStore.auth.user.phoneNumber,
            rootStore.parkingFlow.flow.ticketIdsForFlow,
            source,
            cardholderFirstName,
            cardholderLastName,
            cardNumberAlias,
            cardExpDate,
            cardCvcAlias,
            cardBrand,
            fullBillingAddress,
            billingAddressCity,
            billingAddressState,
            modifiedBillingZip,
            billingAddressCountry,
            firstPlateNumber,
            // gclid captured at the ad click (authStore, from _app.jsx handleUtmParameters),
            // forwarded so the parking conversion attributes deterministically.
            rootStore.auth.gclid || (rootStore.auth.user && rootStore.auth.user.gclid) || undefined,
            rootStore.auth.gclidCapturedAt || undefined,
            channel,
            utmCampaign,
            getMessageId()
          )
        } catch (error) {
          self.showErrorToast(error, false)
          self.setIsCityPayBotRunning(false)
        }
      }
    }),

    preAuthorizeChargeWithEvByCardId: flow(function*(cardId, cardCvc) {
      if (self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault) {
        const rootStore = getRoot(self)
        const { isPayNYCFlow } = rootStore.ui
        const source = isPayNYCFlow ? "payNYC" : "webapp"
        // Normalized acquisition channel ('ads'|'email'|'sms'|'organic') resolved from
        // the click id / utm captured at the ad click, forwarded alongside gclid.
        // auth.utmParameters is a frozen {} default, always truthy — `||` would never
        // fall through to the User's utm, which is where handleUtmParameters actually
        // writes fresh utm for logged-in users (see pages/_app.jsx). Check emptiness
        // instead so a logged-in user's real utm_medium is read.
        const utm = !objectIsEmpty(rootStore.auth.utmParameters)
          ? rootStore.auth.utmParameters
          : rootStore.auth.user && rootStore.auth.user.utm
        const channel = resolveChannel({
          gclid: rootStore.auth.gclid || (rootStore.auth.user && rootStore.auth.user.gclid),
          msclkid: rootStore.auth.msclkid || (rootStore.auth.user && rootStore.auth.user.msclkid),
          utm,
          gclidCapturedAt: rootStore.auth.gclidCapturedAt,
        })
        const utmCampaign = (utm && utm.utm_campaign) || undefined
        const firstPlateNumber = rootStore.parkingFlow.flow?.plate?.plateNumber
        try {
          return yield PaymentService.preAuthorizeChargeEvByCardId(
            rootStore.auth.user.sessionToken,
            rootStore.auth.user.email,
            rootStore.auth.user.phoneNumber,
            rootStore.parkingFlow.flow.ticketIdsForFlow,
            source,
            cardId,
            cardCvc,
            firstPlateNumber,
            // gclid captured at the ad click (authStore, from _app.jsx handleUtmParameters),
            // forwarded so the parking conversion attributes deterministically.
            rootStore.auth.gclid || (rootStore.auth.user && rootStore.auth.user.gclid) || undefined,
            rootStore.auth.gclidCapturedAt || undefined,
            channel,
            utmCampaign,
            getMessageId()
          )
        } catch (error) {
          self.showErrorToast(error, false)
          self.setIsCityPayBotRunning(false)
        }
      }
    }),

    // eslint-disable-next-line require-yield
    payCityBot: flow(function*(
      cityPaySessionId,
      paymentId,
      feesAmount,
      cardNumberAlias,
      cardExpDate,
      cardCvcAlias
    ) {
      let timeoutId
      const rootStore = getRoot(self)
      try {
        socketCityPayBot.connect()
        self.setIsCityPayBotRunning(true)
        self.showCityPayError("")
        self.showCityPayNotPaidList([])
        const {
          billingAddressLine1,
          billingAddressLine2,
          billingAddressCity,
          billingAddressZip,
          billingAddressState,
        } = rootStore.payment.cko
        const nonWordOrWhiteSpacePattern = /[^\w\s]/g // matches any character that is not a word character (alphanumeric or underscore) or a whitespace character.
        const modifiedEmail = self.user.email.replace(/(\+.+)(?=@)/, "")
        const addressPattern = /\b(?:new\s+york|ny)\b/gi // Matches "New York" or "NY" case insensitive
        // Include the optional Apt/Unit/Suite (line2) in the single address
        // line CityPay expects (card AVS only checks street number + ZIP).
        const modifiedAddress = [billingAddressLine1, billingAddressLine2]
          .filter(Boolean)
          .join(" ")
          .trim()
          .replace(nonWordOrWhiteSpacePattern, "")
          .replace(addressPattern, "")
        const modifiedCity = billingAddressCity.replace(
          nonWordOrWhiteSpacePattern,
          ""
        )
        const modifiedZip = billingAddressZip.trim()

        const payData = {
          sessionID: cityPaySessionId,
          ticketIDs: rootStore.parkingFlow.flow.ticketIdsForFlow,
          amountsToBeCharged:
            rootStore.parkingFlow.results.selectedPayableAmounts,
          totalAmount: rootStore.parkingFlow.results.totalSelectedPayableAmount,
          firstName: self.cardholderFirstName,
          lastName: self.cardholderLastName,
          email: modifiedEmail,
          phone: parsePhoneNumber(self.user.phoneNumber, "US").nationalNumber,
          address: modifiedAddress,
          city: modifiedCity,
          state: billingAddressState,
          zipCode: modifiedZip,
          creditCard: cardNumberAlias,
          expMonth: cardExpDate.split("/")[0],
          expYear: cardExpDate.split("/")[1],
          cvv: cardCvcAlias,
          plateNumber: rootStore.parkingFlow.flow.plate?.plateNumber,
          plateState: rootStore.parkingFlow.flow.plate?.state,
          actuallyPay: process.env.ENVIRONMENT === "prod",
        }
        socketCityPayBot.emit("pay_ticket", payData)
        yield PaymentService.queueCityPayPaymentWhenSocketIsDown(
          rootStore.auth.user.sessionToken,
          cityPaySessionId
        )
        timeoutId = setTimeout(async () => {
          TRACKING.paynycParking.trackPayFlowFailure(
            rootStore,
            'Service unavailable'
          )
          self.showCityPayError("Service unavailable")
          socketCityPayBot.disconnect()
          self.setIsCityPayBotRunning(false)
          self.showCityPayError("")
        }, process.env.CITYPAY_BOT_TIMEOUT * 1000)
        socketCityPayBot.once("citypay_error", async (socketErrorResult) => {
          const alreadyPaidErrors = []
          const notFoundErrors = []
          let declined = false
          if (socketErrorResult?.error && socketErrorResult.error.length > 0) {
            socketErrorResult.error.forEach((errorObj) => {
              const errorMessage = errorObj.error
              if (
                errorMessage.includes("already paid") ||
                errorMessage.includes("pending payment")
              ) {
                alreadyPaidErrors.push(errorObj)
              } else if (
                errorMessage.includes(
                  "Your payment address could not be verified."
                )
              ) {
                const error = `${errorMessage} Please check your address and try again.`
                self.showErrorToast(error, false)
                self.setIsCityPayBotRunning(false)
              } else if (
                errorMessage.includes(
                  "Payment was declined. Please try again with alternative form of payment or contact technical support."
                ) ||
                errorMessage.includes(
                  "This transaction was declined due to a processor status of 'decline'"
                )
              ) {
                declined = true
              } else if (errorMessage.includes("not found")) {
                notFoundErrors.push(errorObj)
              } else if (errorMessage.includes("blocked")) {
                console.error(`Payment is blocked: ${errorMessage}`)
                self.showCityPayError(errorMessage)
              } else if (errorMessage.includes("booted or towed")) {
                console.error(`Vehicle booted or towed: ${errorMessage}`)
                self.showCityPayError(errorMessage)
              } else if (
                errorMessage ===
                "amountToBeCharged is more than $1 greater than shown on CityPay"
              ) {
                console.error("Amount to be charged > $1.")
                self.showCityPayError(socketErrorResult)
              } else {
                console.error(errorMessage)
                self.showCityPayError(errorMessage)
              }
            })
          }
          if (declined) {
            self.showCityPayError(CITY_PAY_CARD_DECLINED)
          } else if (alreadyPaidErrors.length > 0 || notFoundErrors.length > 0) {
            self.showCombinedErrorsModal(alreadyPaidErrors, notFoundErrors)
          } else if (socketErrorResult?.error) {
            self.showCityPayError(socketErrorResult.error)
          }
          const firstError =
            socketErrorResult?.error?.[0]?.error ||
            socketErrorResult?.error ||
            'citypay_error'
          TRACKING.paynycParking.trackPayFlowFailure(rootStore, firstError)
          clearTimeout(timeoutId)
          socketCityPayBot.disconnect()
          self.setIsCityPayBotRunning(false)
          self.showCityPayError("")
        })

        socketCityPayBot.once("citypay_paid", async (socketSuccessResult) => {
          if (
            socketSuccessResult.notPaidList &&
            socketSuccessResult.notPaidList.length > 0
          ) {
            self.showCityPayNotPaidList(socketSuccessResult.notPaidList)
          }
          clearTimeout(timeoutId)
          socketCityPayBot.disconnect()
          self.setIsCityPayBotRunning(false)
          self.setCityPayConfirmationID(socketSuccessResult.confirmationID)
          // ticketsForFlow throws "No flow type" when the parking flow type is
          // unset (e.g. reset between starting the CityPay bot and receiving
          // citypay_paid). This async socket callback runs outside the wrapping
          // try/catch, so the throw would surface as an unhandled rejection —
          // guard on a concrete flow type before reading, matching
          // flow.persistableSlice's hasFlowType guard.
          const flow = rootStore.parkingFlow.flow
          const hasFlowType =
            flow.isPayFlow || flow.isFightFlow || flow.isRobotPayFlow
          if (!hasFlowType) {
            console.warn("citypay_paid received with no parking flow type")
            return
          }
          const tickets = flow.ticketsForFlow
          // fetch charged tickets and save them to the store
          await Promise.all(
            tickets.map((ticket) =>
              ticket.fetchTicket(rootStore.auth.user.sessionToken)
            )
          )
          if (flow.isPayFlow) {
            // This is the confirmed-charge point for Evervault/CityPay. The GA4
            // `purchase` no longer waits for it — flow.trackTickets fires at
            // checkout success, because this callback only lands for ~60% of
            // checkouts (tab closed, socket timeout, flow reset) and GA4 was
            // reporting ~40% fewer parking sales than the database.
            //
            // These calls stay as the safety net for the sessions where
            // trackTickets did not run, and for the trackhome pay_success /
            // GA4 funnel legs, which do still mean "the city payment landed".
            // transaction_id is built exactly as trackTickets builds it, so the
            // dedupe in googleAnalytics.purchase drops the duplicate `purchase`
            // on the normal path.
            const transactionIdentifier = tickets
              .map((ticket) => ticket.objectId)
              .join(" - ")
            const ga4Items = TRACKING.paynycParking.parkingItems(
              tickets,
              flow.type
            )
            const ga4State =
              (flow.plate && flow.plate.state) ||
              (tickets[0] && tickets[0].state) ||
              undefined
            if (rootStore.ui.isPayNYCFlow) {
              TRACKING.paynycParking.paySuccess(
                rootStore.ui.isPayNYCFlow,
                PAYMENT_PROVIDERS.evervault,
                rootStore.parkingFlow.results.paySubtotal,
                rootStore.parkingFlow.results.totalServiceFee,
                {
                  transactionId: transactionIdentifier,
                  items: ga4Items,
                  state: ga4State,
                }
              )
            } else {
              // paynycParking.paySuccess no-ops off paynyctickets.com (its
              // trackhome events are PayNYC-scoped), so fire the GA4 leg on its
              // own here.
              TRACKING.parking.payConfirmed(transactionIdentifier, flow.type, {
                value: rootStore.parkingFlow.results.paySubtotal,
                items: ga4Items,
                state: ga4State,
              })
            }
          }
          flow.nextStep()
        })
      } catch (error) {
        clearTimeout(timeoutId)
        socketCityPayBot.disconnect()
        TRACKING.paynycParking.trackPayFlowFailure(
          getRoot(self),
          error?.message || error || 'citypay_unavailable'
        )
        self.showCityPayError("Service unavailable")
        self.setIsCityPayBotRunning(false)
        self.showCityPayError("")
      }
    }),
    checkout: flow(function* checkoutGen(options = {}) {
      const { useFlowSubmit = false } = options
      // dont do anything if we are loading anything
      if (self._isLoading) {
        console.error("Checkout no-op: request already loading in progress")
        return false
      }
      // check if user has toggled TOS
      if (self.isTosRequired) {
        self.setTosError()
      }
      // if no user make sure registration fields are valid
      if (!self.isLoggedIn) {
        yield self.user.validateRegistrationFields()
      }
      // if changing address check address fields are correct
      if (self.isChangingAddress) {
        self.addressChange.validateFields()
      }
      // if adding payment method validate payment form
      if (self.isAddingNewPaymentMethod) {
        self[self.paymentProcessor].validateForm()
      }
      // if we are not ready to submit dont do anythiung
      if (!self.allowSubmit) {
        console.error(
          `Checkout no-op: allowSubmit=false, reasons: ${self.submitDisabledReasons.join(", ") || "unknown"}`
        )
        return false
      }
      const rootStore = getRoot(self)
      if (
        rootStore.ui.isPayNYCFlow &&
        rootStore.parkingFlow.flow.isPayFlow
      ) {
        TRACKING.paynycParking.payAttempt(
          rootStore.ui.isPayNYCFlow,
          self.user.paymentProcessor.type,
          rootStore.parkingFlow.results.paySubtotal,
          TRACKING.paynycParking.parkingItems(
            rootStore.parkingFlow.flow.ticketsForFlow,
            rootStore.parkingFlow.flow.type
          )
        )
      }
      try {
        self._isLoading = true
        // If not signed in, register user
        if (!self.isLoggedIn) {
          self.reaction()
          if (rootStore.auth.canSignUpWithoutPassword) {
            const temporaryPassword = StringTools.random(15)
            rootStore.auth.user.changeKey("password", temporaryPassword)
            if (rootStore.ui.isPayNYCFlow) {
              // We want to set the userType only if on parking pay flow + paynyc domain
              rootStore.auth.user.changeKey("userType", "payNYC")
            }
          }

          const signedUp = yield rootStore.auth.signup({
            addPaymentProcessor: true,
            locationState: rootStore.parkingFlow.searchCriteria.plateState,
            acceptances: self.tosAcceptances,
          })
          if (!signedUp) {
            throw new Error("Issue creating your account.")
          }
          if (rootStore.ui.isPayNYCFlow && rootStore.auth.user.objectId) {
            TRACKING.identifyUser(rootStore.auth.user.objectId)
          }
        } else {
          UserService.recordAcceptances(self.tosAcceptances, self.user.sessionToken)
        }
        // updater users address and payment method if necessary
        yield self.updateUserAddress()
        if (useFlowSubmit) {
          self._isLoading = false
          return true
        }

        if (
          rootStore.trafficSubmission.isOneStepPaymentPlanCheckout &&
          !self.isAddingNewPaymentMethod
        )
          self.isAddingNewPaymentMethod = true
        yield self.updatePaymentMethod()

        // if 3D Secure flow. (only matters if BT, no stripe support)
        let nonce
        if (self.threeDSecure.isRequired) {
          yield self.threeDSecure.verify()
          if (
            self.threeDSecure.responseData &&
            self.threeDSecure.responseData.nonce
          ) {
            nonce = self.threeDSecure.responseData.nonce
          }
        }

        // CKO Flow obtains its payment token via flowComponent.tokenize() in handleSubmit
        if (
          self.user.paymentProcessor.type === PAYMENT_PROVIDERS.cko ||
          (self.user.paymentProcessor.type === PAYMENT_PROVIDERS.evervault &&
            (rootStore.ui.isTrafficFlow ||
              rootStore.parkingFlow.flow.isFightFlow))
        ) {
          nonce = rootStore.payment.cko.token
        }
        // call checkout function passed to payment store for this flow
        yield self.checkoutFunction(nonce)
      } catch (err) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("Error checking out:", err)
        }
        const all = getRoot(self)
        if (self.user.paymentProcessor.type === PAYMENT_PROVIDERS.cko && self.flowComponent) {
          self.remountFlow()
        }
        console.error(`Error checking out: ${err}`)
        TRACKING.paynycParking.trackPayFlowFailure(all, err)
        all.ui.toasts.addToast({
          message: `Checkout failed. ${err}`,
          type: "ERROR",
        })
        self._isLoading = false
        return false
      }
      self._isLoading = false
      return true
    }),
    updateCityPayModalContent(content) {
      self.cityPayModalContent = content
    },
    teardown() {
      if (!self.paymentProcessor) {
        self.teardownFlow()
        return false
      }
      if (checkoutFormAutosaveDisposer) {
        checkoutFormAutosaveDisposer()
        checkoutFormAutosaveDisposer = null
      }
      self.reaction()
      if (self.threeDSecure.isRequired) {
        self.threeDSecure.teardown()
      }
      self.teardownFlow()
      self[self.paymentProcessor].teardown()
      return self.resetPaymentStore()
    },
    resetPaymentStore() {
      self.setInitialized(false)
      self.isTosRequired = true
      self.agreedToTermsOfService = false
      self.clientToken = null
      self.clientTokenStatus = REQUEST_STATUSES.init
      self.defaultPaymentMethod = null
      self.defaultPaymentMethodStatus = REQUEST_STATUSES.init
      self.cardholderName = ""
      self.cardholderFirstName = ""
      self.cardholderLastName = ""
    },
    toggleAgreedToTerms() {
      self.agreedToTermsOfService = !self.agreedToTermsOfService
      self.setTosError()
    },
    toggleAddPaymentMethod() {
      self.isAddingNewPaymentMethod = !self.isAddingNewPaymentMethod
    },
    setAddingNewPaymentMethod(value) {
      self.isAddingNewPaymentMethod = !!value
    },
    toggleChangeAddress() {
      self.isChangingAddress = !self.isChangingAddress
    },
    setRequireAddress(bool) {
      self.requireAddress = bool
    },
    setIsTosRequired(value) {
      self.isTosRequired = value
    },
    setClientToken(token) {
      self.clientToken = token
    },
    setClientTokenStatus(status) {
      self.clientTokenStatus = status
    },
    setDefaultPaymentMethodStatus(status) {
      self.defaultPaymentMethodStatus = status
    },
    fetchDefaultMethod: flow(function* fetchDefault() {
      try {
        if (!self.isLoggedIn) {
          self.isAddingNewPaymentMethod = true
          return false
        }

        const rootStore = getRoot(self)
        if (shouldSkipDefaultPaymentMethodFetch(self.paymentProcessor, rootStore)) {
          self.isAddingNewPaymentMethod = true
          return false
        }

        if (!self.user.sessionToken) {
          self.isAddingNewPaymentMethod = true
          return false
        }

        if (self.defaultPaymentMethodStatus === REQUEST_STATUSES.loading) {
          return false
        }
        self.setDefaultPaymentMethodStatus(REQUEST_STATUSES.loading)

        self.defaultPaymentMethod = yield PaymentService.getDefaultPaymentMethod(
          self.user.sessionToken,
        )

        self.isAddingNewPaymentMethod =
          !self.defaultPaymentMethod || !self.defaultPaymentMethod.paymentType
        self.setDefaultPaymentMethodStatus(REQUEST_STATUSES.complete)
        return true
      } catch (err) {
        if (process.env.WINIT_ENV !== "test") {
          console.error("error fetching default payment method", err)
        }
        const message = String(err || "")
        const isAuthError = /not authorized|unauthorized/i.test(message)
        if (!isAuthError) {
          const all = getRoot(self)
          all.ui.toasts.addToast({
            message: `Issue fetching default payment method: ${err}`,
            type: "ERROR",
          })
        }
        self.isAddingNewPaymentMethod = true
        self.setDefaultPaymentMethodStatus(REQUEST_STATUSES.error)
        return false
      }
    }),
    updateUserAddress: flow(function* updateUserAddressGen() {
      if (!self.isChangingAddress) {
        return true
      }
      self.user.updateAddress(getSnapshot(self.addressChange))
      const updatedUser = yield self.user.updateUser([
        "address",
        "city",
        "countryState",
        "zipcode",
      ])
      if (updatedUser) {
        TRACKING.payment.updateBillingAddress()
        self.isChangingAddress = false
      } else {
        self.user.restoreLastSavedValue([
          "address",
          "city",
          "countryState",
          "zipcode",
        ])
        throw new Error(
          "There was an issue updating your address, please try again."
        )
      }
      return true
    }),
    updatePaymentMethod: flow(function* updatePaymentMethodGen() {
      if (!self.isAddingNewPaymentMethod) {
        self.isPaymentMethodJustAdded = false
        return true
      }
      let paymentMethodToken
      if (self.paymentProcessor === PAYMENT_PROVIDERS.evervault) {
        if (process.env.ENABLE_CKO_PAYMENTS === "true")
          paymentMethodToken = yield self[PAYMENT_PROVIDERS.cko].tokenize()
        else if (process.env.ENABLE_STRIPE_PAYMENTS === " true")
          paymentMethodToken = yield self[PAYMENT_PROVIDERS.stripe].tokenize()
        else
          paymentMethodToken = yield self[
            PAYMENT_PROVIDERS.braintree
          ].tokenize()
      } else paymentMethodToken = yield self[self.paymentProcessor].tokenize()

      let syncResponse = ""
      const rootStore = getRoot(self)
      // Capture before sync — auth may flip hasPaymentMethod during the await.
      const hadPaymentMethod = rootStore.auth.user?.hasPaymentMethod ?? false
      const userId = rootStore.auth.user?.id
      try {
        syncResponse = yield PaymentService.syncPaymentMethod(
          paymentMethodToken,
          rootStore.auth.user.sessionToken,
          rootStore.auth.user.email // For payNYCFlow when users does not sign in
        )
        self.isPaymentMethodJustAdded = true
        TRACKING.payment.updatePaymentMethod()
        if (userId) {
          trackMetaBrowserAddPaymentInfo(userId, hadPaymentMethod)
        }
      } catch (err) {
        throw new Error(err)
      }
      if (self.paymentProcessor === PAYMENT_PROVIDERS.stripe && syncResponse) {
        try {
          const result = yield self[
            PAYMENT_PROVIDERS.stripe
          ].confirm3DSecureAuthentication(syncResponse)
          if (result.error) {
            throw new Error(
              result.error.message || "3D Secure authentication failed."
            )
          }

          if (
            result.paymentIntent &&
            result.paymentIntent.status !== "succeeded"
          ) {
            throw new Error("3D Secure authentication did not succeed.")
          }
        } catch (err) {
          throw err
        }
      }
      if (
        syncResponse !== "" &&
        self.paymentProcessor !== PAYMENT_PROVIDERS.stripe
      )
        throw new Error(syncResponse)

      yield self.fetchDefaultMethod()
      return true
    }),
    setDefaultCkoPaymentMethod: flow(function* setDefaultCkoPaymentMethodGen() {
      const rootStore = getRoot(self)
      let syncResponse = ""
      const paymentMethodToken = yield self[PAYMENT_PROVIDERS.cko].tokenize()
      if (!paymentMethodToken) throw new Error("Select a saved card or add a new one.")
      try {
        syncResponse = yield PaymentService.updateDefaultPaymentMethodForCko(
          paymentMethodToken,
          rootStore.auth.user.sessionToken,
          rootStore.auth.user.email
        )
      } catch (err) {
        throw new Error("Failed to set your default payment method.")
      }
      if (syncResponse !== "") throw new Error(syncResponse)
      return true
    }),
    setInitialized(value) {
      self.initialized = !!value
    },
    setIsACH(value) {
      self.isACH = value
    },
    setBankAccountLinked(value) {
      self.bankAccountLinked = value
    },
    setBankAccountPaymentMethod(value) {
      self.bankAccountPaymentMethod = value
    },
    setTosError() {
      if (!self.agreedToTermsOfService) {
        self.tosError = "You must agree to the terms of service."
      } else {
        self.tosError = null
      }
    },
    showErrorToast(message, autoClose) {
      const rootStore = getRoot(self)
      rootStore.ui.toasts.addToast({
        message,
        type: "ERROR",
        options: {
          autoClose,
        },
      })
    },
    showCityPayError(error) {
      if (error && error.error && error.error.length > 0) {
        const amountToBeChargedError = error.error.find(
          (err) =>
            err.error ===
            "amountToBeCharged is more than $1 greater than shown on CityPay"
        )
        if (amountToBeChargedError) {
          self.cityPayError = error
        } else {
          self.cityPayError = error.error
        }
      } else {
        self.cityPayError = error
      }
    },
    showCityPayNotPaidList(notPaidList) {
      self.cityPayNotPaidList = notPaidList
    },
    ...createPaymentFlowActions(self),
    showCombinedErrorsModal(alreadyPaidErrors, notFoundErrors) {
      let combinedErrorMessage = ""
      if (alreadyPaidErrors.length > 0) {
        combinedErrorMessage += "Already Paid:\n"
        alreadyPaidErrors.forEach((error) => {
          combinedErrorMessage += `- Ticket ID ${error.ticketID}: ${error.error}\n`
        })
      }
      if (alreadyPaidErrors.length > 0 && notFoundErrors.length === 0) {
        alreadyPaidErrors.forEach((error) => {
          self.showErrorToast(
            `Ticket ${error.ticketID} has already been paid.`,
            false
          )
        })
        return
      }
      if (notFoundErrors.length > 0 && alreadyPaidErrors.length === 0) {
        self.showCityPayError(notFoundErrors[0].error)
        return
      }
      if (notFoundErrors.length > 0) {
        if (alreadyPaidErrors.length > 0) {
          combinedErrorMessage += "\n"
        }
        combinedErrorMessage += "Not Found:\n"
        notFoundErrors.forEach((error) => {
          combinedErrorMessage += `- Ticket ID ${error.ticketID}: ${error.error}\n`
        })
      }
      self.showCityPayError(combinedErrorMessage)
    },
  }))
